import '../styles/globals.css';

import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';

import { ReactElement } from 'react';
import { NavProvider } from '../components/layouts/nav/context';

// init globals
if (typeof window !== 'undefined') {
  window.app = { firstLaunch: false, isLaunching: false, hasLaunched: false, loggingOut: false };
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
}

// dynamic data
const AppLaunch = dynamic(() => import('../components/hooks/useAppLaunch').then(m => m.AppLaunch), { ssr: false });
const TrackingScripts = dynamic(() => import('../scripts/tracking').then(m => m.TrackingScripts), { ssr: false });
const Notifications = dynamic(() => import('../components/persistent/notifications').then(m => m.Notifications), {
  ssr: false,
});

// layouts
const DefaultLayout = dynamic(() => import('../components/layouts/default').then(m => m.DefaultLayout));
const DashboardLayout = dynamic(() => import('../components/layouts/dashboard').then(m => m.DashboardLayout));
const AdminLayout = dynamic(() => import('../components/layouts/admin').then(m => m.AdminLayout));
const AdminReferralLayout = dynamic(
  () => import('../components/layouts/admin-referrals').then(m => m.AdminReferralLayout),
  { ssr: false },
);
const PartnerReferralLayout = dynamic(
  () => import('../components/layouts/referrals').then(m => m.PartnerReferralLayout),
  { ssr: false },
);
const InventoryLayout = dynamic(() => import('../components/layouts/inventory').then(m => m.InventoryLayout), {
  ssr: false,
});

function MyApp({ Component, pageProps, router }: AppProps) {
  const getLayout = (page: ReactElement) => {
    const path = router.pathname;

    if (path.startsWith('/embed')) return <>{page}</>;
    if (path.startsWith('/referrals')) return <PartnerReferralLayout>{page}</PartnerReferralLayout>;
    if (path.startsWith('/staff/referrals')) return <AdminReferralLayout>{page}</AdminReferralLayout>;
    if (path.startsWith('/staff/inventory')) return <InventoryLayout>{page}</InventoryLayout>;
    if (path.startsWith('/admin')) return <AdminLayout>{page}</AdminLayout>;
    if (path.startsWith('/dashboard')) return <DashboardLayout>{page}</DashboardLayout>;
    return <DefaultLayout>{page}</DefaultLayout>;
  };

  return (
    <NavProvider>
      {/* UI Layout */}
      {getLayout(<Component {...pageProps} />)}
      {/* End UI Layout */}

      {/* Persistent Floating UI */}
      <Notifications />
      {/* End Persistent Floating UI */}

      {/* Scripts */}
      <AppLaunch />
      <TrackingScripts />
      {/* End Scripts */}
    </NavProvider>
  );
}

export default MyApp;
